/*
  Library of form components (built using base Chakra UI components)
  Each component is designed to accept a single React state - an object containing all form data
    const [formValues, setFormValues] = useState({
      name: '',
      email: '',
      phone: ''
    })
    // { name: 'Bob', email: 'bob@example.com', phone: '+1234567890' }
  Each component also accepts an object containing error messages for all form data
    const [errors, setErrors] = useState({
      name: '',
      email: '',
      phone: ''
    })
    // { name: '', email: 'Email is required', phone: 'Not a valid phone number' }
  To override the styling of a component, pass in the corresponding Chakra UI props
    <DateTimeFormField formLabelProps={{ alignContent: 'center', fontSize: 'sm' }} />
*/

import isNaN from 'lodash/isNaN'
import React from 'react'
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftAddon,
  Textarea,
  Select
} from '@chakra-ui/react'
import { formatDateForInput, formatForDatetimeInput } from 'lib/date-time'

export type FieldProps<T> = {
  field: keyof T
  label: string
  isRequired: boolean
  isDisabled?: boolean
  errors: Record<keyof T, string>
  formControlProps?: React.ComponentProps<typeof FormControl>
  formLabelProps?: React.ComponentProps<typeof FormLabel>
}

export type BaseFormFieldProps<T> = FieldProps<T> & {
  children: React.ReactNode
}

export type EditableFormFieldProps<T> = FieldProps<T> & {
  data: T
  setData: (data: T) => void
}

export type InputFormFieldProps<T> = EditableFormFieldProps<T> & {
  inputGroupProps?: React.ComponentProps<typeof InputGroup>
  inputProps?: React.ComponentProps<typeof Input>
}

export type NumberInputFormFieldProps<T> = InputFormFieldProps<T> & {
  min?: number
  max?: number
}

export type TextareaFormFieldProps<T> = EditableFormFieldProps<T> & {
  textareaProps?: React.ComponentProps<typeof Textarea>
}

export type DropdownFormFieldProps<T> = EditableFormFieldProps<T> & {
  options: { label: string; value: string }[]
  optionsPlaceholder?: string
  selectProps?: React.ComponentProps<typeof Select>
}

export const BaseFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  children,
  errors,
  formControlProps,
  formLabelProps
}: BaseFormFieldProps<T>) => (
  <FormControl
    isRequired={isRequired}
    isInvalid={!!errors[field]}
    isDisabled={isDisabled}
    {...formControlProps}
  >
    <FormLabel {...formLabelProps}>{label}</FormLabel>
    {children}
    <FormErrorMessage>{errors[field]}</FormErrorMessage>
  </FormControl>
)

export const DateFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  errors,
  data,
  setData,
  formControlProps,
  formLabelProps,
  inputProps
}: InputFormFieldProps<T>) => (
  <BaseFormField
    field={field}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    errors={errors}
    formControlProps={formControlProps}
    formLabelProps={formLabelProps}
  >
    <Input
      value={formatDateForInput(data[field] as Date)}
      onChange={(e) =>
        setData({
          ...data,
          [field]: e.target.value ? new Date(e.target.value) : null
        })
      }
      type='date'
      {...inputProps}
    />
  </BaseFormField>
)

export const DateTimeFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  errors,
  data,
  setData,
  formControlProps,
  formLabelProps,
  inputProps
}: InputFormFieldProps<T>) => (
  <BaseFormField
    field={field}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    errors={errors}
    formControlProps={formControlProps}
    formLabelProps={formLabelProps}
  >
    <Input
      value={formatForDatetimeInput(data[field] as Date | null)}
      onChange={(e) =>
        setData({
          ...data,
          [field]: e.target.value ? new Date(e.target.value) : null
        })
      }
      type='datetime-local'
      {...inputProps}
    />
  </BaseFormField>
)

export const CurrencyFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  errors,
  data,
  setData,
  formControlProps,
  formLabelProps,
  inputProps,
  inputGroupProps,
  min,
  max
}: NumberInputFormFieldProps<T>) => (
  <BaseFormField
    field={field}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    errors={errors}
    formControlProps={formControlProps}
    formLabelProps={formLabelProps}
  >
    <InputGroup {...inputGroupProps}>
      <InputLeftAddon>$</InputLeftAddon>
      <Input
        value={(data[field] as number) || ''}
        onChange={(e) => {
          const val = Number(e.target.value)
          if (isNaN(val)) {
            setData({ ...data, [field]: null })
          } else if (max !== undefined && val > max) {
            setData({ ...data, [field]: max })
          } else if (min !== undefined && val < min) {
            setData({ ...data, [field]: min })
          } else {
            setData({ ...data, [field]: val })
          }
        }}
        type='number'
        {...inputProps}
      />
    </InputGroup>
  </BaseFormField>
)

export const TextareaFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  errors,
  data,
  setData,
  formControlProps,
  formLabelProps,
  textareaProps
}: TextareaFormFieldProps<T>) => (
  <BaseFormField
    field={field}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    errors={errors}
    formControlProps={formControlProps}
    formLabelProps={formLabelProps}
  >
    <Textarea
      value={(data[field] as string) || ''}
      onChange={(e) =>
        setData({
          ...data,
          [field]: e.target.value
        })
      }
      {...textareaProps}
    />
  </BaseFormField>
)

export const DropdownFormField = <T,>({
  field,
  label,
  isRequired,
  isDisabled,
  errors,
  data,
  setData,
  options,
  optionsPlaceholder,
  formControlProps,
  formLabelProps,
  selectProps
}: DropdownFormFieldProps<T>) => (
  <BaseFormField
    field={field}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    errors={errors}
    formControlProps={formControlProps}
    formLabelProps={formLabelProps}
  >
    <Select
      value={(data[field] as string) || ''}
      onChange={(e) =>
        setData({
          ...data,
          [field]: e.target.value
        })
      }
      {...selectProps}
    >
      <option value=''>{optionsPlaceholder ?? 'Select an option'}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
  </BaseFormField>
)
